import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { KeyRound, CheckCircle, XCircle } from 'lucide-react';

const ResetPassword = () => {
  const [accessToken, setAccessToken] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('form'); // form, submitting, success
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase appends the recovery session to the URL hash after verifying the link
    const params = new URLSearchParams(window.location.hash.substring(1));
    const token = params.get('access_token');
    const type = params.get('type');

    if (!token) {
      setTokenError('This reset link is invalid or has expired. Please request a new one.');
    } else if (type && type !== 'recovery') {
      setTokenError('This link is not a password-reset link.');
    } else {
      setAccessToken(token);
    }
  }, []);

  const validate = () => {
    if (password.length < 8) return 'Password must be at least 8 characters long.';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter.';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number.';
    if (password !== confirm) return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }

    setError('');
    setStatus('submitting');
    try {
      const response = await fetch('/api/auth/update-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: accessToken, password }),
      });
      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setTimeout(() => navigate('/login?reset=true'), 3000);
      } else {
        setStatus('form');
        setError(data.detail || 'Failed to update password.');
      }
    } catch (err) {
      setStatus('form');
      setError(`Error: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              status === 'success' ? 'bg-green-100' : tokenError ? 'bg-red-100' : 'bg-blue-100'
            }`}>
              {status === 'success'
                ? <CheckCircle className="w-8 h-8 text-green-600" />
                : tokenError
                  ? <XCircle className="w-8 h-8 text-red-600" />
                  : <KeyRound className="w-8 h-8 text-blue-600" />}
            </div>
          </div>
          <CardTitle className="text-2xl">
            {status === 'success' ? 'Password Updated' : 'Reset Your Password'}
          </CardTitle>
          {status !== 'success' && !tokenError && (
            <CardDescription>Choose a new password for your account.</CardDescription>
          )}
        </CardHeader>

        <CardContent>
          {tokenError ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>❌ {tokenError}</AlertDescription>
              </Alert>
              <Button className="w-full" onClick={() => navigate('/auth/forgot-password')}>
                Request a new reset link
              </Button>
            </div>
          ) : status === 'success' ? (
            <div className="space-y-4 text-center">
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-800">
                  ✅ Password updated successfully.
                </AlertDescription>
              </Alert>
              <p className="text-gray-600">Redirecting you to the login page…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter a new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={status === 'submitting'}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <Input
                  id="confirm"
                  type="password"
                  placeholder="Re-enter the new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={status === 'submitting'}
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                At least 8 characters, with an uppercase letter, a lowercase letter, and a number.
              </p>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Updating…' : 'Update Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
